<template>
  <a-form-model
    ref="ruleFormRef"
    :model="localForm"
    :label-col="currentFormLayout.labelCol"
    :wrapper-col="currentFormLayout.wrapperCol"
  >
    <template v-if="isRole">
      <a-form-model-item
        label="角色名称"
        prop="name"
        :rules="[{ required: true, message: '请输入角色名称', trigger: 'blur' }]"
      >
        <a-input
          v-model="localForm.name"
          allow-clear
          :maxLength="10"
          placeholder="请输入"
          style="max-width: 300px;"
        />
      </a-form-model-item>

      <a-form-model-item label="数据权限" :colon="false" :required="false">
        <a-radio-group v-model="localForm.dataPermission">
          <a-radio :value="1">
            全部数据
          </a-radio>
          <a-radio :value="2">
            仅本账号数据
          </a-radio>
        </a-radio-group>
      </a-form-model-item>
    </template>

    <a-form-model-item label="菜单权限">
      <a-checkbox v-model="checkAll" @change="onOptionsChange">
        全局全选
      </a-checkbox>
    </a-form-model-item>

    <a-form-model-item
      v-for="(groupItem, index) in localOptions"
      :key="index"
      :label="groupItem.title"
      class="perm-group"
    >
      <div class="perm-group__head">
        <a-checkbox
          v-model="groupItem.checkAll"
          @change="e => onCheckAllChange(e, groupItem)"
        >
          全选
        </a-checkbox>
      </div>

      <div class="perm-group__body">
        <PermissionFlowItem
          v-for="childItem in getGroupItems(groupItem)"
          :key="childItem.permissionId"
          :child-item="childItem"
          :parent-group="groupItem"
          :group-index="index"
          :flatten="flatten"
          @child-change="onChildChangeEvent"
          @action-change="onActionChangeEvent"
        />
      </div>
    </a-form-model-item>

    <a-form-model-item :wrapper-col="currentFormLayout.offsetCol">
      <a-button
        v-if="showSaveButton"
        type="primary"
        style="width: 140px"
        :loading="loading"
        @click="handleSubmit"
      >
        保存设置
      </a-button>
      <a-button
        v-if="showBackButton"
        style="margin-left: 20px;width: 140px"
        @click="$emit('back')"
      >
        返回
      </a-button>
    </a-form-model-item>
  </a-form-model>
</template>

<script>
import ruleFormLayout from '@/utils/mixins/ruleFormMixins'

import PermissionFlowItem from './PermissionFlowItem.vue'
import { PermissionHelper } from '@/utils/NewPermissionHelper'

export default {
  name: 'RoleEditorView',
  components: {
    PermissionFlowItem
  },
  mixins: [ruleFormLayout],
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    roleId: {
      type: [String,Number,null,undefined],
      default: null
    },
    // 权限菜单树（由父页面生成/回显后传入）
    menuTree: {
      type: Array,
      required: true
    },
    // 一维选中权限ID集合（菜单ID + 按钮ID）；不传则不做回填
    selectedKeys: {
      type: Array,
      default: undefined
    },
    isRole: {
      type: Boolean,
      default: true
    },
    showBackButton: {
      type: Boolean,
      default: true
    },
    // 只读展示场景（如「项目所有页面权限」）可隐藏保存按钮
    showSaveButton: {
      type: Boolean,
      default: true
    },
    // true：流式平铺（仅显示叶子菜单）；false：显示全部层级（默认）
    flatten: {
      type: Boolean,
      default: false
    },
    initForm: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      checkAll: false,
      localOptions: [],
      localForm: {
        name: '',
        dataPermission: 1,
        roleId: null
      }
    }
  },
  watch: {
    // 父级菜单树变化：同步到本地副本后重新回填勾选
    menuTree: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localOptions = val ? JSON.parse(JSON.stringify(val)) : []
        this.normalizeOptions(this.localOptions)
        this.applySelectKeys()
      }
    },
    // 一维选中权限集合变化（如商户类型切换）时按它重新回填勾选状态
    selectedKeys: {
      immediate: true,
      deep: true,
      handler() {
        this.applySelectKeys()
      }
    },
    initForm: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.localForm.name = val.name
          this.localForm.dataPermission = val.dataPermission
          this.localForm.roleId = val.roleId
        }
      }
    }
  },
  methods: {
    /**
     * 保证所有节点的 permissionList / checkAll / checked 均已存在
     * Vue2 只能侦测已存在属性的赋值，缺失属性需显式补上才能保持响应式
     */
    normalizeOptions(list) {
      const loop = (children) => {
        children.forEach((child) => {
          if (!child.permissionList) {
            this.$set(child, 'permissionList', [])
          }
          if (child.actions) {
            child.actions.forEach((act) => {
              if (act.checked === undefined) {
                this.$set(act, 'checked', false)
              }
            })
          }
          if (child.children && child.children.length) {
            loop(child.children)
          }
        })
      }
      list.forEach((group) => {
        if (!group.permissionList) {
          this.$set(group, 'permissionList', [])
        }
        if (group.checkAll === undefined) {
          this.$set(group, 'checkAll', false)
        }
        if (group.children && group.children.length) {
          loop(group.children)
        }
      })
    },

    // 同步分组全选、全局全选状态
    syncGroupCheckAll() {
      if (!this.localOptions) {
        return
      }
      this.localOptions.forEach((group) => {
        if (group.children) {
          group.checkAll = this.recursiveAllChecked(group.children, group.permissionList || [])
        }
      })
      this.$nextTick(() => {
        this.checkAll = this.localOptions.every(g => g.checkAll)
      })
    },

    // ===================== 选中状态回显 =====================
    // 顶层分组节点（带 checkAll 标记），分组自身的 permissionId 不参与勾选
    isGroupOption(item) {
      return 'checkAll' in item
    },

    // 清空节点及其所有子级的勾选状态（自身权限集合 + 按钮勾选）
    resetChildChecked(item) {
      item.permissionList = []
      if (item.actions) {
        item.actions.forEach((act) => {
          act.checked = false
        })
      }
      if (item.children) {
        item.children.forEach(child => this.resetChildChecked(child))
      }
    },

    /**
     * 依据一维权限ID集合，回填单个节点及其子级的勾选状态
     * @param item 当前节点
     * @param groupPermissionList 所属顶层分组的 permissionList（页面 checkbox 的勾选依据，全组共用）
     * @param permissionList 已选中的一维权限ID集合（菜单ID + 按钮权限ID）
     * @returns 当前节点（含其整棵子树）是否视为选中
     */
    fillChildSelectKeys(item, groupPermissionList, permissionList) {
      // 1、菜单自身是否在回显集合里
      let isSelected = permissionList.includes(item.permissionId)

      // 2、回显按钮/操作权限；勾选任一按钮即视为该菜单被选中（与 onActionChangeEvent 行为保持一致）
      if (item.actions && item.actions.length) {
        item.permissionList = []
        item.actions.forEach((act) => {
          act.checked = permissionList.includes(act.permissionId)
          if (!act.checked) {
            return
          }
          isSelected = true
          if (!item.permissionList.includes(act.permissionId)) {
            item.permissionList.push(act.permissionId)
          }
          if (!groupPermissionList.includes(act.permissionId)) {
            groupPermissionList.push(act.permissionId)
          }
        })
      }

      // 3、递归回显下级菜单；后端回显集合可能只给到叶子菜单，子级全选时父级也要回显为选中
      if (item.children && item.children.length) {
        let childrenAllSelected = true
        item.children.forEach((child) => {
          const childSelected = this.fillChildSelectKeys(child, groupPermissionList, permissionList)
          if (!childSelected) {
            childrenAllSelected = false
          }
        })
        if (childrenAllSelected) {
          isSelected = true
        }
      }

      if (isSelected && !groupPermissionList.includes(item.permissionId)) {
        groupPermissionList.push(item.permissionId)
      }
      return isSelected
    },

    /**
     * 将后端返回的一维权限ID集合回显到权限树上（支持三级及无限层级）
     * 传入集合为空即清空全部勾选，调用方不需要回填时不传 selectedKeys 即可
     * @param options 权限树（顶层分组数组）
     * @param permissionList 已选中的一维权限ID集合（菜单ID + 按钮权限ID）
     */
    fillSelectKeys(options, permissionList) {
      if (!options || !options.length) {
        return
      }
      const hasSelected = !!permissionList && permissionList.length > 0
      options.forEach((option) => {
        // 顶层分组：先清空全组勾选，再按传入的权限集合重新回填，避免切换时残留
        if (this.isGroupOption(option)) {
          this.$set(option, 'permissionList', [])
          option.children.forEach(child => this.resetChildChecked(child))
          if (hasSelected) {
            option.children.forEach(child => this.fillChildSelectKeys(child, option.permissionList, permissionList))
          }
          return
        }
        // 兼容直接传入子菜单数组的场景：勾选集挂在节点自身的 permissionList 上
        this.resetChildChecked(option)
        if (hasSelected) {
          this.fillChildSelectKeys(option, option.permissionList, permissionList)
        }
      })
      this.syncGroupCheckAll()
    },

    // ===================== 流式布局：叶子菜单展开 =====================
    // 流式布局下不展示层级，只渲染「没有子级」的叶子菜单（有子级的节点仅用于父级勾选推导）
    getLeafItems(group) {
      const leaves = []
      const walk = (list) => {
        list.forEach((item) => {
          if (item.children && item.children.length) {
            walk(item.children)
          } else {
            leaves.push(item)
          }
        })
      }
      walk((group && group.children) || [])
      return leaves
    },

    // 依据展示模式返回分组下的渲染节点：默认显示全部层级，flatten 时只取叶子菜单平铺
    getGroupItems(group) {
      if (this.flatten) {
        return this.getLeafItems(group)
      }
      return (group && group.children) || []
    },

    // 只要父组件传入了 selectedKeys 就按它回填勾选状态；未传入则沿用 menuTree 自带的勾选状态
    applySelectKeys() {
      if (!this.selectedKeys) {
        this.syncGroupCheckAll()
        return
      }
      this.fillSelectKeys(this.localOptions, this.selectedKeys)
    },

    // ====================== 原有核心递归逻辑【完全保留不变】 ======================
    // 收集所有的权限ID
    recursiveCollectPermId(list, target = []) {
      list.forEach((item) => {
        if (item.permissionId) {
          target.push(item.permissionId)
        }
        if (item.actions && item.actions.length) {
          item.actions.forEach(act => target.push(act.permissionId))
        }
        if (item.children && item.children.length) {
          this.recursiveCollectPermId(item.children, target)
        }
      })
      return target
    },

    recursiveSyncCheck(item, checked, parentTopList, childParentList) {
      const topIdx = parentTopList.indexOf(item.permissionId)
      if (checked && topIdx === -1) {
        parentTopList.push(item.permissionId)
      }
      if (!checked && topIdx > -1) {
        parentTopList.splice(topIdx, 1)
      }

      const selfIdx = childParentList.indexOf(item.permissionId)
      if (checked && selfIdx === -1) {
        childParentList.push(item.permissionId)
      }
      if (!checked && selfIdx > -1) {
        childParentList.splice(selfIdx, 1)
      }

      if (item.actions && item.actions.length) {
        item.permissionList = []
        item.actions.forEach((act) => {
          act.checked = checked
          if (checked) {
            item.permissionList.push(act.permissionId)
          }
        })
      }

      if (item.children && item.children.length) {
        item.permissionList = []
        item.children.forEach((child) => {
          this.recursiveSyncCheck(child, checked, parentTopList, item.permissionList)
        })
      }
    },

    recursiveAllChecked(list, topList) {
      return list.every((item) => {
        if (!topList.includes(item.permissionId)) {
          return false
        }
        if (item.children && item.children.length) {
          return this.recursiveAllChecked(item.children, topList)
        }
        return true
      })
    },

    // ====================== 父级菜单勾选状态动态同步 ======================
    // 子级全选则父级自动勾选，否则取消父级勾选；必须后序遍历，保证深层派生结果能参与上层判断
    recursiveSyncParentChecked(list, groupPermissionList) {
      list.forEach((item) => {
        if (!item.children || !item.children.length) {
          return
        }
        this.recursiveSyncParentChecked(item.children, groupPermissionList)
        const allChildrenChecked = item.children.every(child => groupPermissionList.includes(child.permissionId))
        const selfIdx = groupPermissionList.indexOf(item.permissionId)
        if (allChildrenChecked && selfIdx === -1) {
          groupPermissionList.push(item.permissionId)
        } else if (!allChildrenChecked && selfIdx > -1) {
          groupPermissionList.splice(selfIdx, 1)
        }
      })
    },

    // 勾选变更后，同步当前分组内所有父级菜单的勾选状态，并重算分组/全局全选
    syncParentCheckedState(groupItem) {
      if (!groupItem.permissionList) {
        this.$set(groupItem, 'permissionList', [])
      }
      this.recursiveSyncParentChecked(groupItem.children || [], groupItem.permissionList)
      this.syncGroupCheckAll()
    },

    // ====================== 事件处理器 ======================
    onCheckAllChange(e, groupItem) {
      const checked = e.target.checked
      this.$set(groupItem, 'permissionList', [])
      groupItem.children.forEach((child) => {
        child.permissionList = []
        this.recursiveSyncCheck(child, checked, groupItem.permissionList, child.permissionList)
      })
      this.syncParentCheckedState(groupItem)
    },

    onChildChangeEvent(payload) {
      const { e, groupItem, childItem } = payload
      const checked = e.target.checked
      this.recursiveSyncCheck(childItem, checked, groupItem.permissionList, childItem.permissionList)
      this.syncParentCheckedState(groupItem)
    },

    onActionChangeEvent(payload) {
      const { e, groupItem, childItem, actionItem } = payload
      const checked = e.target.checked
      actionItem.checked = checked
      if (!childItem.permissionList) {
        this.$set(childItem, 'permissionList', [])
      }
      if (!groupItem.permissionList) {
        this.$set(groupItem, 'permissionList', [])
      }
      if (actionItem.permissionId && childItem.actions) {
        const actIdx = childItem.permissionList.indexOf(actionItem.permissionId)
        if (checked && actIdx === -1) {
          // 选中,权限不存在，直接追加
          childItem.permissionList.push(actionItem.permissionId)
        }
        if (checked && !groupItem.permissionList.includes(actionItem.permissionId)) {
          groupItem.permissionList.push(actionItem.permissionId)
        }
        const otherCheckedActionsPermissionList = childItem.actions.filter(action => action.key !== actionItem.key && action.checked).map(a => a.permissionId)
        // 先判断其他的有没有选中
        const hasCheck = otherCheckedActionsPermissionList.includes(actionItem.permissionId)
        if (!checked && actIdx > -1) {
          if (!hasCheck) {
            // 其他操作不依赖时，删除当前依赖
            childItem.permissionList.splice(actIdx, 1)
          }
        }
      }
      if (checked && !groupItem.permissionList.includes(childItem.permissionId)) {
        groupItem.permissionList.push(childItem.permissionId)
      }
      this.syncParentCheckedState(groupItem)
    },

    onOptionsChange(e) {
      const checked = e.target.checked
      this.localOptions.forEach((group) => {
        this.$set(group, 'permissionList', [])
        group.children.forEach((child) => {
          child.permissionList = []
          this.recursiveSyncCheck(child, checked, group.permissionList, child.permissionList)
        })
        this.syncParentCheckedState(group)
      })
    },

    // 表单校验完成，向外抛保存事件，子组件不直接请求接口
    handleSubmit() {
      if (!this.$refs.ruleFormRef) {
        return
      }
      this.$refs.ruleFormRef.validate((valid) => {
        if (!valid) {
          this.$message.error('请输入角色名称')
          return
        }
        const permissionList = PermissionHelper.getPermissionList(this.localOptions)
        if (!permissionList || permissionList.length < 1) {
          this.$message.error('请选中需要显示的菜单')
          return
        }
        const stringify = JSON.stringify(this.localOptions)
        this.$emit('save', this.isRole
          ? {
            authority: stringify,
            permissionList: permissionList,
            roleName: this.localForm.name,
            dataPermission: this.localForm.dataPermission
          }
          : JSON.parse(stringify))
      })
    },

    // 供父页面（如「项目所有页面权限」导出 json）读取当前权限树
    getTreeOptionsList() {
      return JSON.parse(JSON.stringify(this.localOptions))
    }
  }
}
</script>

<style scoped lang='less'>
// 流式布局：分组标题走 form 的 label 列，勾选项平铺换行
.perm-group {
  margin-bottom: 8px;

  &__head {
    line-height: 32px;
  }

  // 分组内容：叶子节点横向平铺，带下级的节点由 .flow-item--branch 独占一行
  &__body {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 2px 20px; // 行间距 2px、列间距 20px
    padding-bottom: 10px;
  }
}
</style>
